import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import { SavedBillingViews } from '../components/billing/BillingWorkflowTools'
import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Stack,
  Input,
  Select,
  Option,
  Checkbox,
  CircularProgress,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
} from '@mui/joy'
import {
  Plus as Add,
  MagnifyingGlass as Search,
  Envelope as Email,
  DotsThreeVertical as MoreVert,
  Trash as Delete,
  PencilSimple as Edit,
  Eye as Visibility,
  Printer as Print,
  CheckSquare as CheckBox,
  CurrencyDollar as AttachMoney,
  ArrowsLeftRight as SwapHoriz,
} from '@phosphor-icons/react'
import { estimatesApi } from '../lib/billingApi'
import { formatCurrency, getStatusColor, formatStatus } from '../lib/billingUtils'
import { ResponsiveTable } from '../components/common/ResponsiveTable'
import type { Estimate, EstimateFilters, DocumentStatus } from '../types/billing'

export default function EstimateList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Filter state
  const [filters, setFilters] = useState<EstimateFilters>({
    status: searchParams.get('status') as DocumentStatus || undefined,
    search: searchParams.get('search') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    expired_only: searchParams.get('expired_only') === 'true',
  })

  // Selection state
  const [selectedEstimates, setSelectedEstimates] = useState<number[]>([])
  const [bulkStatusModal, setBulkStatusModal] = useState(false)
  const [bulkStatus, setBulkStatus] = useState<DocumentStatus>('sent')
  useEffect(() => {
    setFilters({ ...Object.fromEntries(searchParams),
      status: searchParams.get('status') || undefined,
      search: searchParams.get('search') || '',
      date_from: searchParams.get('date_from') || '', date_to: searchParams.get('date_to') || '',
      overdue_only: searchParams.get('overdue_only') === 'true',
      expired_only: searchParams.get('expired_only') === 'true',
    } as any)
    setCurrentPage(1)
  }, [searchParams])
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 20

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filters.status, filters.search, filters.date_from, filters.date_to, filters.expired_only])

  // Fetch estimates
  const { data, isLoading, error } = useQuery({
    queryKey: ['estimates', filters, currentPage],
    queryFn: () => estimatesApi.list({ ...filters, page: currentPage }),
  })

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ['estimate-summary', filters],
    queryFn: () => estimatesApi.summary(filters),
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: number[], status: DocumentStatus }) =>
      estimatesApi.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      queryClient.invalidateQueries({ queryKey: ['estimate-summary'] })
      setSelectedEstimates([])
      setBulkStatusModal(false)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => estimatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      queryClient.invalidateQueries({ queryKey: ['estimate-summary'] })
      toast.success('Estimate deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete estimate')
    },
  })

  // Email mutation - temporarily disabled, need to add email dialog
  // const emailMutation = useMutation({
  //   mutationFn: (id: number) => estimatesApi.sendEmail(id, emailData),
  // })

  // Convert to invoice mutation
  const convertMutation = useMutation({
    mutationFn: (id: number) => estimatesApi.convertToInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })

  const handleFilterChange = (newFilters: Partial<EstimateFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)

    // Update URL params
    const params = new URLSearchParams()
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value) params.set(key, String(value))
    })
    setSearchParams(params)
  }

  const handleSelectAll = () => {
    if (selectedEstimates.length === data?.results.length) {
      setSelectedEstimates([])
    } else {
      setSelectedEstimates(data?.results.map(est => est.id) || [])
    }
  }

  const handleSelectEstimate = (id: number) => {
    setSelectedEstimates(prev =>
      prev.includes(id)
        ? prev.filter(estId => estId !== id)
        : [...prev, id]
    )
  }


  if (error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading estimates. Please try again.
      </Alert>
    )
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 1.5, md: 3 } }}>
      <SavedBillingViews kind="estimates"/>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: { xs: 3, md: 4 }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Typography level="h3">Estimates</Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Create and manage project estimates
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            component={Link}
            to="../invoices"
            variant="outlined"
            color="primary"
            startDecorator={<AttachMoney />}
          >
            View Invoices
          </Button>
          <Button
            component={Link}
            to="new"
            variant="solid"
            color="primary"
            startDecorator={<Add />}
          >
            New Estimate
          </Button>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      {summary && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="primary">
              <CardContent>
                <Typography level="body-sm">Total Value</Typography>
                <Typography level="h3">{formatCurrency(summary?.total_amount)}</Typography>
                <Typography level="body-sm">{summary?.estimate_count || 0} estimates</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="success">
              <CardContent>
                <Typography level="body-sm">Accepted</Typography>
                <Typography level="h3">{formatCurrency(summary?.accepted_amount)}</Typography>
                <Typography level="body-sm">{summary?.accepted_count || 0} estimates</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="warning">
              <CardContent>
                <Typography level="body-sm">Pending</Typography>
                <Typography level="h3">{formatCurrency(summary?.pending_amount)}</Typography>
                <Typography level="body-sm">{summary?.pending_count || 0} estimates</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="soft" color="danger">
              <CardContent>
                <Typography level="body-sm">Expired</Typography>
                <Typography level="h3">{formatCurrency(summary?.expired_amount)}</Typography>
                <Typography level="body-sm">{summary?.expired_count || 0} estimates</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControl size="sm" sx={{ minWidth: 200 }}>
              <Input
                placeholder="Search estimates..."
                value={filters.search}
                onChange={(e) => handleFilterChange({ search: e.target.value })}
                startDecorator={<Search />}
              />
            </FormControl>

            <FormControl size="sm" sx={{ minWidth: 150 }}>
              <Select
                placeholder="Status"
                value={filters.status}
                onChange={(_, value) => handleFilterChange({ status: value as DocumentStatus })}
              >
                <Option value="">All Statuses</Option>
                <Option value="draft">Draft</Option>
                <Option value="sent">Sent</Option>
                <Option value="viewed">Viewed</Option>
                <Option value="accepted">Accepted</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="expired">Expired</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="From date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange({ date_from: e.target.value })}
              />
            </FormControl>

            <FormControl size="sm">
              <Input
                type="date"
                placeholder="To date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange({ date_to: e.target.value })}
              />
            </FormControl>

            <Checkbox
              label="Expired only"
              checked={filters.expired_only}
              onChange={(e) => handleFilterChange({ expired_only: e.target.checked })}
              size="sm"
            />

            <Button
              variant="outlined"
              color="primary"
              size="sm"
              onClick={() => {
                setFilters({})
                setSearchParams(new URLSearchParams())
              }}
            >
              Clear Filters
            </Button>
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>

      {/* Bulk Actions */}
      {selectedEstimates.length > 0 && (
        <Alert
          color="primary"
          variant="soft"
          sx={{ mb: 2 }}
          startDecorator={<CheckBox />}
          endDecorator={
            <Stack direction="row" spacing={1}>
              <Button
                size="sm"
                variant="solid"
                onClick={() => setBulkStatusModal(true)}
              >
                Update Status
              </Button>
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                startDecorator={<Email />}
                onClick={() => {
                  // Implement bulk email
                }}
              >
                Send Email
              </Button>
            </Stack>
          }
        >
          {selectedEstimates.length} estimate{selectedEstimates.length > 1 ? 's' : ''} selected
        </Alert>
      )}

      {/* Estimate Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data?.results && data.results.length > 0 ? (
        <ResponsiveTable<Estimate>
          rows={data.results}
          getRowKey={(estimate) => estimate.id}
          columns={[
            {
              key: 'select',
              header: (
                <Checkbox
                  checked={selectedEstimates.length === data.results.length && data.results.length > 0}
                  indeterminate={selectedEstimates.length > 0 && selectedEstimates.length < data.results.length}
                  onChange={handleSelectAll}
                />
              ),
              mobileLabel: 'Select',
              width: '50px',
              render: (estimate) => (
                <Checkbox
                  checked={selectedEstimates.includes(estimate.id)}
                  onChange={() => handleSelectEstimate(estimate.id)}
                />
              ),
            },
            {
              key: 'document_number',
              sortValue: (row) => row.document_number,
              header: 'Estimate #',
              mobileVariant: 'header',
              render: (estimate) => (
                <Link to={`${estimate.id}`}>
                  <Typography level="title-sm">{estimate.document_number}</Typography>
                </Link>
              ),
            },
            {
              key: 'customer',
              sortValue: (row) => row.customer_detail?.last_name ?? row.customer_detail?.first_name,
              header: 'Customer',
              render: (estimate) => (
                <Box>
                  <Typography level="body-sm">
                    {estimate.customer_detail
                      ? `${estimate.customer_detail.first_name} ${estimate.customer_detail.last_name}`
                      : 'N/A'}
                  </Typography>
                  {estimate.job_name && (
                    <Typography level="body-xs" color="neutral">
                      {estimate.job_name}
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              key: 'date',
              sortValue: (row) => row.document_date ? new Date(row.document_date) : null,
              header: 'Date',
              render: (estimate) => (
                <Typography level="body-sm">
                  {new Date(estimate.document_date).toLocaleDateString()}
                </Typography>
              ),
            },
            {
              key: 'valid_until',
              sortValue: (row) => row.valid_until ? new Date(row.valid_until) : null,
              header: 'Valid Until',
              render: (estimate) => (
                <Box>
                  <Typography level="body-sm">
                    {estimate.valid_until ? new Date(estimate.valid_until).toLocaleDateString() : 'N/A'}
                  </Typography>
                  {estimate.is_expired && (
                    <Typography level="body-xs" color="danger">
                      Expired
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              key: 'amount',
              sortValue: (row) => Number(row.total_amount),
              header: 'Amount',
              render: (estimate) => (
                <Typography level="body-sm" fontWeight="lg">
                  {formatCurrency(estimate.total_amount || '0')}
                </Typography>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (estimate) => (
                <Chip size="sm" color={getStatusColor(estimate.status)} variant="soft">
                  {formatStatus(estimate.status)}
                </Chip>
              ),
            },
          ]}
          rowActions={(estimate) => (
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: 'plain', size: 'sm' } }}
              >
                <MoreVert />
              </MenuButton>
              <Menu size="sm" placement="bottom-end">
                <MenuItem component={Link} to={`${estimate.id}`}>
                  <Visibility /> View
                </MenuItem>
                <MenuItem component={Link} to={`${estimate.id}/edit`}>
                  <Edit /> Edit
                </MenuItem>
                {estimate.acceptance_status === 'accepted' && !estimate.converted_to_invoice && (
                  <MenuItem onClick={() => convertMutation.mutate(estimate.id)}>
                    <SwapHoriz /> Convert to Invoice
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    estimatesApi
                      .generatePdf(estimate.id)
                      .then((data) => {
                        const link = document.createElement('a')
                        link.href = data.file_url
                        link.download = `estimate_${estimate.document_number || estimate.id}.pdf`
                        link.target = '_blank'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                        toast.success('PDF downloaded')
                      })
                      .catch(() => toast.error('Failed to download PDF'))
                  }}
                >
                  <Print /> Print
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this estimate?')) {
                      deleteMutation.mutate(estimate.id)
                    }
                  }}
                  sx={{ color: 'danger.main' }}
                >
                  <Delete /> Delete
                </MenuItem>
              </Menu>
            </Dropdown>
          )}
        />
      ) : (
        <Card variant="soft">
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography level="body-lg" sx={{ mb: 2 }}>No estimates found</Typography>
            <Button
              component={Link}
              to="new"
              startDecorator={<Add />}
              variant="solid"
            >
              Create Your First Estimate
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mt: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Typography level="body-sm" color="neutral">
          {(data?.count ?? 0) > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, data!.count)}–${Math.min(currentPage * PAGE_SIZE, data!.count)} of ${data!.count} estimates`
            : 'No estimates'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Stack>

      {/* Bulk Status Update Modal */}
      <Modal open={bulkStatusModal} onClose={() => setBulkStatusModal(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Update Estimate Status</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Update status for {selectedEstimates.length} selected estimate{selectedEstimates.length > 1 ? 's' : ''}
            </Typography>
            <FormControl>
              <FormLabel>New Status</FormLabel>
              <Select value={bulkStatus} onChange={(_, value) => setBulkStatus(value as DocumentStatus)}>
                <Option value="draft">Draft</Option>
                <Option value="sent">Sent</Option>
                <Option value="accepted">Accepted</Option>
                <Option value="rejected">Rejected</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setBulkStatusModal(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              onClick={() => bulkUpdateMutation.mutate({ ids: selectedEstimates, status: bulkStatus })}
              loading={bulkUpdateMutation.isPending}
            >
              Update Status
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  )
}

// Grid component (since Joy UI doesn't have Grid, we'll use Box with flex)
const Grid: React.FC<{
  children: React.ReactNode
  container?: boolean
  spacing?: number
  xs?: number
  sm?: number
  md?: number
  lg?: number
}> = ({ children, container, spacing = 0, xs, sm, md, lg }) => {
  if (container) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: spacing ? -spacing / 2 : 0,
        }}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: spacing ? spacing / 2 : 0,
        width: {
          xs: xs ? `${(xs / 12) * 100}%` : '100%',
          sm: sm ? `${(sm / 12) * 100}%` : undefined,
          md: md ? `${(md / 12) * 100}%` : undefined,
          lg: lg ? `${(lg / 12) * 100}%` : undefined,
        },
      }}
    >
      {children}
    </Box>
  )
}
